<template>
  <net-item
    v-if="element != null"
    :uid="nodeUid"
    :item-style="{
      backgroundColor: fillColor || 'var(--color-background-deep)'
    }"
    :item-class="isActive ? 'scene-element is-active' : 'scene-element'"
    :size="size"
    :node-uid="nodeUid"
    :selected="isSelected"
    :faded="isFaded"
    :highlighted="isHighlighted"
    :focussed="state.focussed"
    :fixed="isFixed"
    :locked="locked"
    :disabled="disabled"
    :min-width="minWidth"
    :min-height="minHeight"
    @drag-start="onDragStart"
    @drag-end="onDragEnd"
    @select="select($event)"
  >
    <template #before>
      <button
        v-if="features.momentum && momentum"
        class="scene-element__momentum"
        type="button"
        :disabled="disabled"
        @click="select(momentum)"
      >
        <scene-momentum
          :net-mode="netMode"
          :element-position="size"
          :x="-10"
          :y="0"
          :element-type="momentum.elementType"
          :element-id="momentum.elementId"
          :selection="selection"
        />
      </button>

      <template
        v-if="netMode == 'edit' && isOnlySelected && !isInConnectionMode && !dragging"
      >
        <stakeholder-popup-menu
          v-if="features.stakeholder && stakeholderInPopup != null"
          :stakeholder-id="stakeholderInPopup"
          :x="size.x + size.width + 125"
          :y="size.y + size.height"
          @select="select"
          @close="closeStakeholderPopup"
        />

        <index-popup-menu
          v-if="
            (state.popupOpen || stakeholderInPopup != null) &&
            element.type == 'index'
          "
          :index-id="element.id"
          :x="size.x + size.width"
          :y="size.y + size.height"
          @select="select"
          @close="closePopupMenu"
        />
        <target-popup-menu
          v-else-if="
            (state.popupOpen || stakeholderInPopup != null) &&
            element.type == 'target'
          "
          :target-id="element.id"
          :x="size.x + size.width"
          :y="size.y + size.height"
          @select="select"
          @close="closePopupMenu"
        />
        <resource-popup-menu
          v-else-if="
            (state.popupOpen || stakeholderInPopup != null) &&
            element.type == 'resource'
          "
          :resource-id="element.id"
          :x="size.x + size.width"
          :y="size.y + size.height"
          @select="select"
          @close="closePopupMenu"
        />
      </template>
    </template>

    <div
      class="scene-element__main"
      ref="dropzoneRef"
      :class="{ '-ready-for-drop': isOverDropZone }"
      :style="{
        '--title-height': `${titleHeight}px`,
        '--footer-height': `${footerHeight}px`
      }"
    >
      <button
        class="scene-element__selectbutton"
        @focus="onFocus"
        @blur="onBlur"
        @click="onFocusButtonClick"
      >
        {{ $t("actions.select") + ":" + title }}
      </button>

      <header
        v-if="!nodeElementData.headlineHidden"
        class="scene-element__headline"
        :style="{ maxHeight: `${titleHeight}px`, height: `${titleHeight}px` }"
      >
        <net-input-text
          ref="titleInput"
          :id="`target-title-${element.id}`"
          :value="title"
          :disabled="netMode !== 'edit'"
          :placeholder="$t('labels.unnamed')"
          :style="{ color: textColor, textAlign: sceneNode?.textAlign || 'left', fontSize: titleFontSize + 'px', maxHeight: `${titleHeight}px` }"
          class="scene-element__title"
          :max-lines="titleMaxLines"
          :position="{ x: size.x, y: size.y }"
          :text-options="{
            textColor: sceneNode?.textColor,
            textAlign: sceneNode?.textAlign,
            fontSize: titleFontSize
          }"
          @update:text-option="onTextOptionUpdate"
          @enter="focusNextElement"
          @update:value="updateTitle($event)"
          @focus="onFocus"
          @blur="onBlur"
          @enter-edit-mode="emit('update:tool', 'textedit')"
          @leave-edit-mode="emit('update:tool', 'pointer')"
          @pointerdown="onTextPointerDown($event)"
        />
        <div v-if="titleTakenMultipleTimes && editable" class="scene-element__title-warning" :title="$t('messages.titleDuplicated')">
          <lucide-icon name="triangle-alert" />
        </div>
      </header>

      <img
        v-if="image != null"
        class="scene-element__image"
        :src="imageData"
        :width="size.width"
        :height="imageHeight"
        draggable="false"
      />

      <footer
        class="scene-element__footer"
        :style="{
          height: `${footerHeight}px`
        }"
      >
        <div v-if="netMode == 'choose'" class="scene-element__iconsegment">
          <div class="scene-element__icon" :class="{ '-filled': isSelected }">
            <lucide-icon v-if="isSelected" name="check" />
          </div>
        </div>

        <div
          v-else-if="isInConnectionMode && !isConnectionSourceElement && element.type != 'index'"
          class="scene-element__iconsegment"
        >
          <div
            class="scene-element__icon"
            :class="{ '-filled': impactToSourceElement != null }"
          >
            <lucide-icon v-if="connectionIcon" :name="connectionIcon" />
          </div>
        </div>

        <div
          v-else-if="displayMode === 'impacts' || isConnectionSourceElement"
          class="scene-element__relationscounter"
        >
          <span>
            <lucide-icon
              name="arrow-right"
              class="scene-element__relationscounter__icon"
            />
            <span>{{ incomingRelations.length }}</span>
          </span>
          <span>
            <span>{{ outgoingRelations.length }}</span>
            <lucide-icon
              name="arrow-right"
              class="scene-element__relationscounter__icon"
            />
          </span>
        </div>

        <div
          v-else-if="displayMode === 'value' && rangeAvailable"
          class="scene-element__rangesegment mt-auto"
          :class="{'-influencing': netMode == 'influence'}"
        >
          <input-range
            v-if="
              nodeElementData.barVisible &&
              minMax.maxValue != null &&
              minMax.minValue != null
            "
            :value="Number(valuePlusScheduledEffect)"
            :old-value="Number(calculatedValue)"
            :max="minMax.maxValue"
            :min="minMax.minValue"
            :colors="valueColors"
            :step="valueRangeStep"
            :highlight-changes="netMode == 'influence'"
            class="scene-element__range"
            :class="{ '-width-limited': nodeElementData.barWidthLimited }"
            @update:value="state.localCachedValue = $event"
            @change="updateValue($event)"
          >
            <template #tooltip="{ value: cachedValue }">
              {{ formatNumber(cachedValue) }}<span v-if="unit">&thinsp;{{ unit }}</span>
            </template>
            <template #after="{ value: cachedValue }">
              <span
                v-if="nodeElementData.valueVisible"
                class="scene-element__rangetext"
                :style="{ minWidth: `${2.5 + decimalPoints * 0.35}em` }"
              >
                <net-input-text
                  :id="`target-value-${element.id}`"
                  :value="formatInputValue(cachedValue)"
                  :rendered-value="formatValue(calculatedValue)"
                  :disabled="!valueEditable"
                  :max-lines="1"
                  :position="{ x: size.x, y: size.y }"
                  class="scene-element__rangevalue scene-element__input-field"
                  @update:value="updateValue($event)"
                  @focus="onFocus"
                  @blur="onBlur"
                  @enter="onValueEnter()"
                  @enter-edit-mode="emit('update:tool', 'textedit')"
                  @leave-edit-mode="emit('update:tool', 'pointer')"
                />
                <net-input-text
                  :id="`target-unit-${element.id}`"
                  :value="unit"
                  :disabled="!unitEditable"
                  :max-lines="1"
                  :position="{ x: size.x, y: size.y }"
                  class="scene-element__rangetext__unit scene-element__input-field"
                  @update:value="updateUnit($event)"
                  @focus="onFocus"
                  @blur="onBlur"
                  @enter="onUnitEnter()"
                  @enter-edit-mode="emit('update:tool', 'textedit')"
                  @leave-edit-mode="emit('update:tool', 'pointer')"
                />
              </span>
            </template>
          </input-range>
        </div>

        <div
          v-else-if="displayMode === 'value'"
          class="scene-element__valuesegment"
          :class="{'-influencing': netMode == 'influence'}"
        >
          <net-item-value-plain
            v-if="
              nodeElementData.valueVisible && (
                !nodeElementData.barVisible ||
                minMax.maxValue == null ||
                minMax.minValue == null
              )
            "
            :value="calculatedValue"
            :unit="unit"
            :max-decimal-points="decimalPoints"
          >
            <template #value v-if="valueEditable">
              <net-input-text
                :id="`target-value-${element.id}`"
                :value="formatInputValue(valuePlusScheduledEffect)"
                :rendered-value="formatValue(calculatedValue)"
                :max-lines="1"
                :line-height="1.25"
                class="scene-element__valueinput scene-element__input-field"
                :title="formatNumber(calculatedValue, { maximumFractionDigits: 4 })"
                :position="{ x: size.x, y: size.y }"
                @update:value="updateValue($event)"
                @focus="onFocus"
                @blur="onBlur"
                @enter="onValueEnter()"
                @pointerdown.stop=""
                @enter-edit-mode="emit('update:tool', 'textedit')"
                @leave-edit-mode="emit('update:tool', 'pointer')"
              />
            </template>
            <template #unit v-if="unitEditable">
              <net-input-text
                :id="`target-unit-${element.id}`"
                :value="unit"
                :max-lines="1"
                :line-height="1.25"
                :position="{ x: size.x, y: size.y }"
                class="net-item-value-plain__unit scene-element__input-field"
                @update:value="updateUnit($event)"
                @focus="onFocus"
                @blur="onBlur"
                @enter="onUnitEnter()"
                @enter-edit-mode="emit('update:tool', 'textedit')"
                @leave-edit-mode="emit('update:tool', 'pointer')"
              />
            </template>
          </net-item-value-plain>
          <net-item-value-bar
            v-else-if="nodeElementData.barVisible && minMax.maxValue != null && minMax.minValue != null"
            :value="calculatedValue"
            :max-value="minMax.maxValue"
            :min-value="minMax.minValue"
            :unit="unit"
            :value-visible="nodeElementData.valueVisible"
            class="scene-element__valuebar"
            :class="{ '-width-limited': nodeElementData.barWidthLimited }"
          >
            <template #unit v-if="unitEditable">
              <net-input-text
                :id="`target-unit-${element.id}`"
                :value="unit"
                :max-lines="1"
                :position="{ x: size.x, y: size.y }"
                class="net-item-value-plain__unit scene-element__input-field"
                @update:value="updateUnit($event)"
                @focus="onFocus"
                @blur="onBlur"
                @enter="onUnitEnter()"
                @enter-edit-mode="emit('update:tool', 'textedit')"
                @leave-edit-mode="emit('update:tool', 'pointer')"
              />
            </template>
          </net-item-value-bar>
        </div>
      </footer>
    </div>

    <div
      v-if="isInMatrix && priorityClass != 'is-undefined'"
      class="scene-element__prioritybox"
      :class="[priorityClass]"
    >
      <div class="scene-element__prioritycircle">
        {{ matrixLabel }}
      </div>
    </div>

    <scene-element-impulses
      v-if="netMode != 'edit' && element.type != 'index' && (impulse || openElementEffects.length > 0 || cost || cachedValueDifference != 0)"
      :item-type="element.type"
      :item-id="element.id"
      :impulse="impulse"
      :effects="openElementEffects"
      :open-cost="cost"
      :value="calculatedValue"
      :extra-value-change="cachedValueDifference"
      :max-decimal-points="impulseDecimalPoints"
    />
  </net-item>
</template>

<script
  setup
  lang="ts"
  generic="T extends TargetItem | ResourceItem | IndexItem"
>
import { watch, computed, reactive, inject, ref, onMounted, toRefs } from "vue";
import { useRoute } from "vue-router";
import { useDropZone } from "@vueuse/core";

import eventHub from "@/helpers/utility/Events";
import { fileToDataURL, getDataUrlDimensions, warnIfFileIsLarge } from "@/helpers/utility/File";
import { ensureFloat } from "@/helpers/utility/Type";
import { formatNumber } from "@/helpers/utility/Formatting";
import { getContrastColor, useColor } from "@/helpers/utility/Color";
import { isEmpty } from "@/helpers/utility/Text";
import { focusNextElement } from "@/helpers/utility/Focus";
import { keyElementValues, keyNetCanvas } from "@editor/net/netCanvasInjectionKeys";
import { normalizeIdentity, splitUid } from "@/helpers/utility/Id";

import { useImageStore } from "@editor/items/image/images.store";
import { useUndo } from "@editor/store/undo/undo.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useMomentumStore } from "@editor/items/momentum/momentums.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useElements } from "@editor/items/element/elements";
import { useSelectedImpactMatrix } from "../impactMatrix/useImpactMatrix";
import { useFeatures } from "@editor/store/features.store";
import { useSceneTree } from "../scene/sceneTree";
import { useSceneNodesStore } from "../scene/sceneNodes.store";

import NetItem from "@editor/net/NetItem.vue";
import NetInputText from "@editor/net/NetInputText.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import InputRange from "@core/input/InputRange.vue";
import NetItemValueBar from "@editor/net/NetItemValueBar.vue";
import SceneElementImpulses from "@editor/items/sceneElement/SceneElementImpulses.vue";
import SceneMomentum from "@editor/items/momentum/SceneMomentum.vue";
import NetItemValuePlain from "@editor/net/NetItemValuePlain.vue";

import IndexPopupMenu from "../index/IndexPopupMenu.vue";
import TargetPopupMenu from "../target/TargetPopupMenu.vue";
import ResourcePopupMenu from "../resource/ResourcePopupMenu.vue";
import StakeholderPopupMenu from "../stakeholder/StakeholderPopupMenu.vue";

import type { Uid } from "@/helpers/utility/Id";
import type { ElementItem } from "@editor/items/element/Element";
import type { TargetItem } from "@editor/items/target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { IndexItem } from "../index/IndexItem";
import type { CanvasSelection, NetCanvasProps } from "@editor/net/NetCanvasProps";
import type { NetToolKey } from "@editor/net/NetTools";
import type { Handleable } from "@core/types/Events";
import type { TextOptions } from "@editor/net/netTextToolbarContext";
import type { SceneTreeNode } from "../scene/SceneTreeItem";
import type { SceneElementData } from "./SceneElementData";
import type { RotatableRect, SceneNodeItem } from "../scene/SceneNodeItem";
import { onUnmounted } from "vue";

interface Props {
  nodeUid: Uid<"sceneNode">;
  netMode: "choose" | "edit" | "view" | "influence";
  size: RotatableRect;

  selection: CanvasSelection;
  tool: NetToolKey;

  editable?: boolean;
  disabled?: boolean;
  locked?: boolean;

  impactSourceElement?: { uid: Uid<"target" | "resource" | "index"> };
  flowSourceElement?: { uid: Uid<"target" | "resource"> };
  fixedItem?: { uid: Uid<"target" | "resource" | "index"> };
}
const props = withDefaults(defineProps<Props>(), {
  editable: false,
  disabled: false,
  locked: false,
});

const emit = defineEmits<{
  (e: "toggle-relation-mode", item: { type: ElementItem["type"]; id: number; uid: ElementItem["uid"] }): void;
  (e: "toggle-flow-mode", item: { uid: Uid<"target" | "resource"> }): void;
  (e: "select", payload: { uid: Uid<"sceneNode" | "momentum" | "flow">; }): void;
  (e: "update:value", payload: { uid: Uid<ElementItem["type"]>; value: number }): void;
  (e: "wheel", event: WheelEvent): void;
  (e: "update:tool", tool: NetToolKey): void;
}>();

const route = useRoute();
const features = useFeatures();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const indexStore = useIndexStore();
const relationStore = useRelationStore();
const imageStore = useImageStore();
const sceneTree = useSceneTree();
const elements = useElements();
const sceneNodeStore = useSceneNodesStore();

const dropzoneRef = ref<HTMLElement>();
const titleInput = ref<InstanceType<typeof NetInputText>>();

const state = reactive({
  gridSize: 10,
  focussed: false,
  localCachedValue: 0 as number | string,
  popupOpen: false
});

const injectedElementValues = inject(keyElementValues);
const elementValues = computed(() => ({
  ...(injectedElementValues?.value || {})
}));

const injectedCanvas = inject(keyNetCanvas);
const canvasProps = computed((): NetCanvasProps => injectedCanvas?.value.props as NetCanvasProps || {} as NetCanvasProps);
const {
  effectValues,
  selection,
  impulses,
  openEffects,
  elementCosts,
} = toRefs(canvasProps.value);

onMounted(() => {
  setTimeout(() => {
    if (isSelected.value && props.editable && isEmpty(title.value)) {
      titleInput.value?.switchToEditMode();
    }
  }, 10);
});

// ====================
// NODE & ELEMENT DATA

const treeNode = computed(() => {
  return sceneTree.treeNodes.get(props.nodeUid) as SceneTreeNode<"element"> | undefined;
});

const sceneNode = computed(() => treeNode.value?.node);

const element = computed((): TargetItem | ResourceItem | IndexItem | null => {
  const node = sceneNode.value;
  if (!node) return null;
  const data = node.data;
  if (!data) return null;
  return elements.byUid[data.elementUid];
});

const nodeElementData = computed((): Partial<SceneElementData> => {
  const node = sceneNode.value;
  if (!node) return {};
  return node.data || {};
});

// ====================
// TITLE

const title = computed(() => {
  return element.value?.title ?? "";
});

function updateTitle(title: string) {
  if (!element.value) return;
  if (element.value.type === "target") {
    targetStore.update({
      id: element.value.id,
      title
    });
  } else if (element.value.type === "resource") {
    resourceStore.update({
      id: element.value.id,
      title
    });
  } else if (element.value.type === "index") {
    indexStore.update({
      id: element.value.id,
      title
    });
  }
}

const titleTakenMultipleTimes = computed(() => {
  const elementsWithTitle = elements.byTitle[title.value] || [];
  return elementsWithTitle.length > 1;
});

// ====================
// VALUE CALCULATIONS

const valueGroup = computed(() => {
  if (!element.value) return null;
  return elementValues.value[element.value.uid];
});

const calculatedValue = computed(() => {
  if (valueGroup.value != null) {
    if (valueGroup.value.valid === false) return null;
    return valueGroup.value.value;
  }
  return startingValue.value;
});
const startingValue = computed(() => {
  if (!element.value) return 0;
  if ("value" in element.value) return element.value.value;
  return 0;
});

const openElementEffects = computed(() => {
  if (!element.value) return [];
  return openEffects.value[element.value.type]?.[element.value.id] || [];
});

const valuePlusScheduledEffect = computed(() => {
  const value = calculatedValue.value;
  if (value == null) return null;

  const scheduledEffects = openElementEffects.value.filter((effect) => effect.type == "scheduledEffect");
  if (scheduledEffects.length === 0) return value;

  let summedValue = scheduledEffects.reduce((acc, effect) => {
    if (effectValues.value[effect.id] == null) return acc;
    return Number(acc) + effectValues.value[effect.id];
  }, value);

  if (minMax.value.minValue != null) {
    summedValue = Math.max(Number(summedValue), minMax.value.minValue);
  }
  if (minMax.value.maxValue != null) {
    summedValue = Math.min(Number(summedValue), minMax.value.maxValue);
  }
  
  return summedValue;
});

const cachedValueDifference = computed(() => {
  if (!element.value) return 0;
  if (state.localCachedValue == null) return 0;
  if (valuePlusScheduledEffect.value == null) return 0;
  if (valuePlusScheduledEffect.value === false) return 0;

  return Number(state.localCachedValue) - Number(valuePlusScheduledEffect.value);
});

const minMax = computed(() => {
  if (valueGroup.value != null) {
    return {
      minValue: valueGroup.value.minValue,
      maxValue: valueGroup.value.maxValue
    };
  }
  return { minValue: 0, maxValue: 0 };
});

const valueEditable = computed(() => {
  if (!element.value) return false;
  return element.value.type !== "index" && (props.netMode == "edit" || props.netMode == "influence");
});

const rangeAvailable = computed(() => {
  return (
    valueEditable.value &&
    nodeElementData.value.barVisible &&
    minMax.value.maxValue != null &&
    minMax.value.minValue != null
  );
});

const unit = computed(() => {
  if (!element.value) return "";
  if (element.value.unit) {
    return element.value.unit;
  }

  if (element.value.type == "index") {
    const inferredUnit = elementValues.value[element.value.uid]?.unit;
    return inferredUnit || "";
  }
  return "";
});

const unitEditable = computed(() => {
  if (!element.value) return false;
  return props.netMode === "edit" && element.value.type != "target";
});

const valueRangeStep = computed(() => {
  if (decimalPoints.value > 0) {
    return 1 / Math.pow(10, decimalPoints.value);
  }
  return 1;
});

function updateValue(value: number | string) {
  if (!element.value) return;
  state.localCachedValue = value;
  if (value === "") return;

  let numberValue = ensureFloat(value.toString().replace(/,/g, "."));
  if (isNaN(numberValue) || !isFinite(numberValue)) {
    return;
  }

  // ensure the number is within the min/max range
  if (minMax.value.minValue != null && numberValue < minMax.value.minValue) {
    numberValue = minMax.value.minValue;
    state.localCachedValue = numberValue;
  }
  if (minMax.value.maxValue != null && numberValue > minMax.value.maxValue) {
    numberValue = minMax.value.maxValue;
    state.localCachedValue = numberValue;
  }

  emit("update:value", { uid: element.value.uid, value: numberValue });
}

function updateUnit(unit: string) {
  if (!element.value) return;
  if (element.value.type == "target") return;

  if (element.value.type === "index") {
    indexStore.update({
      id: element.value.id,
      unit: unit || null
    });
  } else if (element.value.type === "resource") {
    resourceStore.update({
      id: element.value.id,
      unit
    });
  }
}

// ====================
// IMPULSE

const impulse = computed(() => {
  if (!element.value) return null;
  const itemImpulses = impulses.value[element.value.type] || {};
  return itemImpulses[element.value.id] || null;
});

// ===================
// COST

const cost = computed(() => {
  if (!element.value) return undefined;
  return elementCosts.value[element.value.uid];
});

// ===================
// MOMENTUM

const momentum = computed(() => {
  if (!element.value) return null;
  const momentum = useMomentumStore().byElementUid[element.value.uid] ?? null;
  if ("momentumActive" in element.value) {
    return element.value.momentumActive ? momentum : null;
  }
  return momentum;
});

// ====================
// STATUS

const isSelected = computed(() => {
  if (!element.value) return false;
  return (treeNode.value && props.selection.selected.has(treeNode.value.uid)) || (element.value && props.selection.selected.has(element.value.uid))
});
const isOnlySelected = computed(
  () => isSelected.value && props.selection.selected.size === 1
);
const isHighlighted = computed(() => {
  if (!element.value) return false;
  return (treeNode.value && props.selection.highlighted.has(treeNode.value.uid)) || (element.value && props.selection.highlighted.has(element.value.uid))
});
const isActive = computed(() => element.value != null && props.selection.active.has(element.value.uid));

/**
 * The element is faded when an other element is selected.
 */
const isFaded = computed(() => {
  if (!element.value) return false;

  // don't fade out while choosing resource connections
  if (props.netMode == "choose") {
    return false;
  }
  if (isInMatrix.value == true) {
    return impactMatrix.activePriorities[element.value.uid] == null;
  }
  // don't fade out if NO element is selected at all:
  if (props.selection.selected.size === 0) return false;
  return !isSelected.value;
});

const isFixed = computed(() => {
  if (!element.value) return false;
  return (
    (props.impactSourceElement?.uid === element.value.uid) ||
    (props.flowSourceElement?.uid === element.value.uid) ||
    (props.fixedItem?.uid === element.value.uid)
  );
});

const isInConnectionMode = computed(() => {
  if (props.netMode !== "edit") return false;
  return props.impactSourceElement != null || props.flowSourceElement != null;
});

const isConnectionSourceElement = computed(() => {
  if (!element.value) return false;
  return (
    (props.impactSourceElement?.uid === element.value.uid) ||
    (props.flowSourceElement?.uid === element.value.uid)
  );
});

const displayMode = computed(() => {
  return sceneTree.activeScene.elementsDisplayMode;
});

const isInMatrix = computed(() => {
  if (route.meta.areaName === "inspector") {
    return useInspectorView().subview === "matrix";
  }
  return false;
});

watch(isSelected, (selected) => {
  if (!selected) {
    titleInput.value?.blur();
  }
});

// ====================
// COLORS

const colorModeStore = useColor();

const currentDefaultBG = computed(() => {
  return colorModeStore.colorMode === 'dark' ? "000" : "#fff";
});

const fillColor = computed(() => {
  if (sceneNode.value?.fill) {
    return sceneNode.value.fill;
  }
  return sceneNode.value?.fill ?? currentDefaultBG.value;
});

const textColor = computed(() => {
  if (sceneNode.value?.textColor) {
    return sceneNode.value.textColor;
  }
  return getContrastColor(fillColor.value);
});

const valueColors = computed(() => {
  return useSettingsStore().targetValueColors;
});

// ====================
// RELATIONS

const items = computed(() => {
  return {
    target: targetStore.items,
    resource: resourceStore.items,
    index: indexStore.items
  };
});

const outgoingRelations = computed(() => {
  if (!element.value) return [];
  const relations = relationStore.activeByStartUid[element.value.uid] ?? [];
  return relations.filter((relation) => {
    const endItem = items.value[relation.endItemType][relation.endItemId];
    if (endItem == null) return false;
    return true;
  });
});
const incomingRelations = computed(() => {
  if (!element.value) return [];
  const relations = relationStore.activeByEndUid[element.value.uid] ?? [];
  return relations.filter((relation) => {
    const startItem = items.value[relation.startItemType][relation.startItemId];
    if (startItem == null) return false;
    return true;
  });
});

const impactToSourceElement = computed(() => {
  if (!element.value) return null;
  if (props.impactSourceElement == null) return null;
  if (isFixed.value) return null;
  if (element.value.type === "index") return null;
  const { type, id } = splitUid(props.impactSourceElement.uid);
  return relationStore.getBetweenItems(
    { id, type },
    { id: element.value.id, type: element.value.type }
  );
});

const connectionIcon = computed(() => {
  if (impactToSourceElement.value == null) return "";
  if (impactToSourceElement.value.updateType == "none") return "x";
  if (impactToSourceElement.value.inverse) return "inverse";
  return "not-inverse";
});

// ====================
// MATRIX

const impactMatrix = useSelectedImpactMatrix();

const matrixLabel = computed(() => {
  if (!element.value) return "";
  return elements.labels[element.value.uid] ?? "";
});
const activePriority = computed(() => {
  if (!element.value) return 0;
  return impactMatrix.activePriorities[element.value.uid];
});
const passivePriority = computed(() => {
  if (!element.value) return 0;
  return impactMatrix.passivePriorities[element.value.uid];
});
const priorityClass = computed(() => {
  let className = "is-undefined";
  const active = activePriority.value;
  const passive = passivePriority.value;
  if (active >= 0.5 && passive >= 0.5) className = "is-critical";
  else if (active >= 0.5 && passive < 0.5) className = "is-active";
  else if (active < 0.5 && passive >= 0.5) className = "is-passive";
  else if (active < 0.5 && passive < 0.5) className = "is-buffering";
  return className;
});

// ====================
// IMAGE

const image = computed(() => {
  if (nodeElementData.value && nodeElementData.value.imageId != null) {
    return imageStore.items[nodeElementData.value.imageId];
  }
  return null;
});

const imageData = computed(() => {
  if (!image.value) return undefined;
  return imageStore.mediaData[image.value.id] ?? undefined;
});

const imageHeight = computed(() => {
  if (!image.value) return 0;
  const aspectRatio = image.value?.aspectRatio ?? 1;
  return (
    Math.ceil(props.size.width / aspectRatio / state.gridSize) * state.gridSize
  );
});

// ====================
// SIZES

const GRID_SIZE = 10;

const minHeight = computed(() => {
  const elementAppearance = nodeElementData.value;

  let minHeight = footerHeight.value;
  
  // add title space
  if (elementAppearance.headlineHidden == false) {
    minHeight += titleHeight.value;
  }

  if (elementAppearance.imageId != null) {
    // add image space
    minHeight += props.size.width / (image.value?.aspectRatio ?? 1);
  }

  // align to grid
  minHeight = Math.ceil(minHeight / GRID_SIZE) * GRID_SIZE;

  return minHeight;
});

const footerHeight = computed(() => {
  const headlineHidden = nodeElementData.value?.headlineHidden ?? false;
  const size = sceneFontSize.value + 16;
  if (headlineHidden) return Math.ceil(size / GRID_SIZE) * GRID_SIZE;
  return size;
});

const titleHeight = computed(() => {
  if (nodeElementData.value.headlineHidden) return 0;
  const titleFontHeight = Math.ceil(titleFontSize.value * TITLE_LINE_HEIGHT / GRID_SIZE) * GRID_SIZE;
  return Math.max(
    props.size.height - imageHeight.value - footerHeight.value,
    titleFontHeight
  );
});

const TITLE_LINE_HEIGHT = 1.15;

const sceneFontSize = computed(() => {
  return sceneTree.activeScene.fontSize;
});

const titleFontSize = computed(() => {
  if (!sceneNode.value) return sceneFontSize.value;
  return sceneFontSize.value * sceneNode.value.fontSizeRelative;
});

const titleMaxLines = computed(() => {
  const titleVerticalPadding = titleFontSize.value * 0.6;
  return Math.max(
    Math.floor(
      (titleHeight.value - titleVerticalPadding) /
        (titleFontSize.value * TITLE_LINE_HEIGHT)
    ),
    1
  );
});

const minWidth = computed(() => {
  return targetStore.minWidth;
});

watch(
  valuePlusScheduledEffect,
  (val) => {
    if (val != null) {
      state.localCachedValue = Number(valuePlusScheduledEffect.value || 0);
    }
  },
  { immediate: true }
);

// ====================
// FOCUS HANDLING

function onFocus() {
  state.focussed = true;
}
function onBlur() {
  state.focussed = false;
}

function onValueEnter() {
  if (state.localCachedValue === "") {
    updateValue(Number(calculatedValue.value ?? 0));
  } else {
    updateValue(state.localCachedValue);
  }
  focusNextElement();
}
function onUnitEnter() {
  focusNextElement();
}

function onFocusButtonClick() {
  select({ uid: props.nodeUid });
  if (props.editable) {
    titleInput.value?.switchToEditMode();
  }
}

function select(item: { uid: Uid<"sceneNode" | "momentum" | "flow" | "stakeholder"> }) {
  const { type, id } = normalizeIdentity(item);
  if (type === "stakeholder") {
    stakeholderInPopup.value = id;
    return;
  }
  if (item.uid === props.nodeUid) {
    // already selected
  }
  emit("select", normalizeIdentity({ type, id }));
}

function onTextPointerDown(event: PointerEvent & Handleable) {
  if (props.netMode !== "edit") return;
  if (!props.editable) return;
  if (state.focussed) return;
  if (isSelected.value) return;
  if (event.hasBeenHandled) return;
  if (event.shiftKey) return;

  event.hasBeenHandled = true;
  event.handledBy = "SceneElement";

  select({ uid: props.nodeUid });
}

// ====================
// DRAG & RESIZE

const dragging = ref(false);

function onDragStart() {
  dragging.value = true;
  // Group the action to make sure the movement of all
  // targets can be undone at once
  useUndo().startGroup("model", "moveElements");
}

function onDragEnd() {
  dragging.value = false;

  // Close the drag action undo group, including
  // the area update
  useUndo().endGroup("model");
}

// ====================
// POPUP MENU

function openPopupMenu() {
  state.popupOpen = true;
}

function closePopupMenu() {
  if (stakeholderInPopup.value != null) return;
  console.log("close popup");
  state.popupOpen = false;
}

function onPopupmenuRequest(uid: string) {
  if (element.value?.uid === uid || props.nodeUid === uid) {
    openPopupMenu();
  }
}

eventHub.on("popupmenu-request", onPopupmenuRequest);
onUnmounted(() => {
  eventHub.off("popupmenu-request", onPopupmenuRequest);
});

// ====================
// STAKEHOLDER POPUP MENU

const stakeholderInPopup = ref<number | null>(null);

function closeStakeholderPopup() {
  console.log("close stakeholder popup");
  stakeholderInPopup.value = null;
}

// ====================
// DROP Media

const { isOverDropZone } = useDropZone(
  () => (props.editable ? dropzoneRef.value : null),
  {
    onDrop: (files: File[] | null, event) => {
      event.stopPropagation();
      event.preventDefault();
      if (!files) {
        return;
      } else {
        updateImage(files[0]);
      }
    },
    dataTypes: [
      "image/svg+xml",
      "image/png",
      "image/jpeg",
      "image/gif",
      "image/*"
    ]
  }
);

async function updateImage(file: File) {
  if (!props.editable) return;

  if (!file.type.startsWith("image/")) {
    throw new Error("Only images are allowed");
  }

  warnIfFileIsLarge(file, 4);

  const dataUrl = await fileToDataURL(file);
  const dimensions = await getDataUrlDimensions(dataUrl, file.type);

  const aspectRatio = dimensions.width / dimensions.height;

  if (nodeElementData.value.imageId) {
    imageStore.update({
      id: nodeElementData.value.imageId,
      data: dataUrl,
      aspectRatio: aspectRatio,
      // mediaType: file.type,
      width: dimensions.width,
      height: dimensions.height
    });
  } else {
    const imageEntry = await imageStore.create({
      data: dataUrl,
      aspectRatio: aspectRatio,
      // mediaType: file.type,
      width: dimensions.width,
      height: dimensions.height
    });
    updateSceneNodeDataKey("imageId", imageEntry.id);
  }
}

function updateSceneNodeDataKey<K extends keyof SceneElementData>(
  keyName: K,
  value: SceneElementData[K]
) {
  if (!treeNode.value) return;
  useSceneNodesStore().updateDataKey<"element">(treeNode.value.id, keyName, value);
}

function formatInputValue(value: number | boolean | null) {
  if (value == null) return "";
  return formatNumber(value, {
    useGrouping: false
  });
}

function formatValue(value: number | boolean | null) {
  if (value == null) return "-";
  return formatNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalPoints.value
  });
}

const decimalPoints = computed(() => {
  if (!element.value) return 0;
  const extra = element.value.type === "index" ? 1 : 0;
  // get the number of decimal points from the max and min value
  let n = Math.max(
    startingValue.value.toString().split(".")[1]?.length ?? 0,
    (minMax.value.maxValue + "").split(".")[1]?.length ?? 0,
    (minMax.value.minValue + "").split(".")[1]?.length ?? 0
  );
  if (n != 0) return Math.min(n, 3);
  const distance = Math.abs((minMax.value.maxValue || 100) - (minMax.value.minValue || 0));
  if (distance <= 1) return 3 + extra;
  if (distance <= 10) return 2 + extra;
  if (distance < 100) return 1 + extra;
  if (distance < 1000) return 0 + extra;
  return 0;
});

const impulseDecimalPoints = computed(() => {
  if (!element.value) return 0;
  // get the number of decimal points from the max and min value
  let n = Math.max(
    startingValue.value.toString().split(".")[1]?.length ?? 0,
    (minMax.value.maxValue + "").split(".")[1]?.length ?? 0,
    (minMax.value.minValue + "").split(".")[1]?.length ?? 0
  );
  if (n > 0) return Math.min(n + 2, 4);
  const distance = Math.abs((minMax.value.maxValue || 100) - (minMax.value.minValue || 0));
  if (distance <= 1) return 4;
  if (distance <= 10) return 3;
  if (distance <= 100) return 2;
  if (distance <= 1000) return 1;
  return 0;
});

function onTextOptionUpdate<K extends keyof TextOptions>(k: K, value: TextOptions[K]) {
  if (!sceneNode.value) return;
  if (k === "fontSize") {
    sceneNodeStore.updateKey(sceneNode.value.id, "fontSizeRelative", (value as TextOptions["fontSize"] ?? sceneTree.activeScene.fontSize) / sceneTree.activeScene.fontSize);
    return;
  }
  if (k in sceneNode.value) {
    const key = k as keyof (SceneNodeItem | TextOptions);
    sceneNodeStore.updateKey(sceneNode.value.id, key, value as any);
  }
}
</script>

<style lang="scss">
.scene-element {
  position: relative;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  background-color: transparent;
  border-radius: 3px;
  outline: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  text-rendering: optimizeLegibility;
  backface-visibility: hidden;
  transition: outline-color 100ms;
  pointer-events: auto;
  font-family: var(--font-family-ui);

  // Do not use 'transform: translate3d(0, 0, 0)' in the base
  // style, because that will render the target blurry.

  &.is-faded {
    outline-color: var(--color-border-light);
  }

  &.is-highlighted {
    outline-color: var(--color-action);
  }

  &.is-selected {
    outline-color: var(--color-action);
    outline-width: 2px;
  }
  &.is-fixed {
    outline-color: var(--color-action);
    outline-width: 4px;
  }

  &.is-active {
    outline-color: var(--color-action);
    outline-width: 3px;
  }

  &.-focussed {
    outline-color: var(--color-action);
  }

  &.-disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  &:hover {
    outline-color: var(--color-action);
  }

  &__main {
    border-radius: inherit;
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;

    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: var(--title-height, min-content) minmax(0, 1fr) var(
        --footer-height,
        0
      );
    grid-template-areas: "header" "image" "footer";

    &.-ready-for-drop {
      // tint in primary color
      outline: 3px solid var(--color-action);
      &::before {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: inherit;
        background-color: hsla(var(--color-primary-h), 80%, 60%, 0.5);
        z-index: 1;
      }
    }
  }

  &.is-faded-out {
    filter: saturate(0.8) contrast(0.6) brightness(1.3);
    box-shadow: none;
  }

  &__headline {
    height: max-content;
    grid-area: header;
    width: 100%;
    hyphens: auto;
    font-weight: 400;
    font-size: 1em;
    padding: 0.45em 0.6em;
    text-rendering: optimizeLegibility;
    backface-visibility: hidden;

    .is-faded & {
      color: var(--color-text-transparent);
    }

    .is-selected &,
    .is-fixed & {
      font-weight: 600;
    }
  }

  &__title {
    position: relative;

    width: 100%;
    // height: 100%;
    grid-template-rows: minmax(0, 1fr);
    max-height: none;
    max-height: 100%;

    top: calc((var(--scene-font-size) - 16) / 16 * -0.3em);

    overflow: hidden;
    padding: 0;
    &.-editing {
      outline: 1px dashed var(--color-action);
      outline-offset: 2px;
    }
  }

  &__selectbutton {
    position: absolute;
    // focussable, but not visible
    width: 1px;
    height: 1px;
    pointer-events: none;
    padding: 0;
    margin: -1px;
    border: 0;
    clip: rect(0 0 0 0);
    overflow: hidden;
  }

  &__image {
    display: block;
    grid-area: image;
    width: 100%;
    object-fit: contain;
    object-position: top center;
  }

  &__footer {
    margin-top: auto;
    grid-area: footer;
    background-color: var(--color-background-close);
    display: grid;
    align-items: end;
    align-content: end;
    border-bottom-right-radius: inherit;
    border-bottom-left-radius: inherit;
  }

  &__relationscounter,
  &__rangesegment,
  &__iconsegment {
    height: calc(1em + 16px);
    background-color: var(--color-background-close);
  }

  &__relationscounter {
    padding: 2px 0.5em;

    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    color: var(--color-text-light);

    & > span {
      display: flex;
      align-items: flex-end;
      gap: 4px;
      font-size: calc(1em + 10px);
    }

    &__icon {
      color: var(--color-text-light);
      display: block;
      position: relative;
      margin-bottom: 1px;
    }

    .is-selected &,
    .is-fixed & {
      color: var(--color-text);
    }
  }

  &:hover &__relationscounter {
    color: var(--color-text);
  }

  &__rangesegment {
    border-radius: inherit;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-left: 16px;
    padding-top: 12px;
    padding: 0.5em 0.6em;
    overflow: hidden;
    z-index: 0;
  }

  &__range {
    font-size: 0.875em;
    opacity: 0.6;
    transition: opacity 100ms;
    pointer-events: none;
    gap: min(0.75em, 12px);

    input {
      pointer-events: auto;
    }

    .is-selected & {
      opacity: 1;
    }

    &.-width-limited {
      max-width: 14em;
    }
  }

  &:hover &__range {
    opacity: 1;
  }

  &__rangetext {
    position: relative;
    color: var(--color-text-transparent);
    margin-top: -3px;
    font-size: calc(1em + 6px);
    font-weight: 600;
    transition: color 100ms;
    text-align: center;
    white-space: nowrap;
    line-height: 1em;
    flex-shrink: 0;
    backface-visibility: hidden;

    display: block;
    white-space: nowrap;
    align-items: flex-end;
    gap: 2px;

    &__unit {
      pointer-events: all;
      font-size: 0.75em;
      min-width: 0.5em;
      font-weight: 400;
      display: inline-grid;
    }
  }

  &__rangevalue {
    pointer-events: all;
    margin-bottom: -0.05em;
    min-width: 1.25em;
    text-align: right;
    padding-right: 0.125em;
    display: inline-grid;
  }

  

  &__valuesegment {
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 100%;
    overflow: hidden;
    height: calc(1em + 16px);
    background-color: var(--color-background-close);
    border-radius: inherit;
    padding: 0.5em 0.6em;
    overflow: hidden;
  }

  &__valueinput {
    display: inline-grid;
    padding-right: 0.125em;
  }

  
  &__valuesegment.-influencing &__valueinput.-editing,
  &__rangesegment.-influencing &__rangevalue.-editing {
    color: var(--color-action);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &__input-field {
    &:hover:not(:focus-within):not(.-disabled) {
      outline: 2px solid var(--color-action-light);
      outline-offset: 1px;
      background-color: var(--color-background);
      border-radius: 2px;
    }
  }


  &__iconsegment {
    display: flex;
    justify-content: center;
    align-items: flex-end;
  }

  &__icon {
    display: block;
    border-radius: 60px;
    border: 2px solid var(--color-action);
    width: calc(1em + 14px);
    height: calc(1em + 14px);
    font-size: 1em;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2px;
    margin-top: -2px;
    background-color: var(--color-background-close);

    &.-filled {
      color: var(--color-action-contrast);
      background-color: var(--color-action);
    }
  }

  &__prioritybox {
    --prio-color: black;
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 1px solid var(--prio-color);
    border-radius: inherit;

    &.is-active {
      --prio-color: var(--color-priority-active);
    }
    &.is-critical {
      --prio-color: var(--color-priority-critical);
    }
    &.is-buffering {
      --prio-color: var(--color-priority-buffering);
    }
    &.is-passive {
      --prio-color: var(--color-priority-passive);
    }
    &.is-undefined {
      --prio-color: var(--color-background);
    }
  }

  &__prioritycircle {
    border-radius: 100px;
    background-color: var(--prio-color);
    font-size: calc(1em + 6px);
    height: calc(1em + 14px);
    width: calc(1em + 14px);
    position: absolute;
    bottom: -12px;
    left: -21px;
    color: #222;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 400;
    padding-bottom: 0.25em;
  }


  &__momentum {
    position: absolute;
    top: -22px;
    left: -22px;
    background: none;
    border: none;
    padding: 0;
  }

  &__title-warning {
    position: absolute;
    top: 4px;
    right: 4px;
    background-color: var(--color-warning);
    color: var(--color-warning-dark);
    border-radius: 100px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: bold;
  }

  &__transition {
    &-enter-active,
    &-leave-active {
      transition:
        opacity 50ms ease-out,
        transform 50ms ease-out;
    }

    &-enter-from,
    &-leave-to {
      transform: scale(0.8);
      opacity: 0;
    }
  }
}
</style>
